import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import formatDateTime from "../../utils/formatDateTime";
import { navigateToTop } from "../../utils/Constants";
import { downloadFile } from "../../utils/download";
import { API_BASE } from "../../config/apiBase";
import ConfirmDialog from "../../component/ui/ConfirmDialog";
import "../../styles/pages/interview/_recording-detail.scss";

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtTimestamp(seconds) {
    if (seconds === null || seconds === undefined || Number.isNaN(seconds))
        return "—";
    const total = Math.max(0, Math.floor(Number(seconds)));
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function prettyTitle(slug) {
    if (!slug) return "Recording";
    return slug.replaceAll("-", " ");
}

// ─── Icons ────────────────────────────────────────────────────────────────────
const IconDownload = () => (
    <svg
        width="15"
        height="15"
        viewBox="0 0 15 15"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M7.5 2v8M4.5 7.5l3 3 3-3M2 13h11"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const IconTrash = () => (
    <svg
        width="15"
        height="15"
        viewBox="0 0 15 15"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M2 4h11M5 4V2.5a.5.5 0 0 1 .5-.5h4a.5.5 0 0 1 .5.5V4M5.5 6.5v4M9.5 6.5v4M2.5 4l.9 8a.5.5 0 0 0 .5.5h7.2a.5.5 0 0 0 .5-.5l.9-8"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const IconLock = () => (
    <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        aria-hidden="true"
    >
        <rect
            x="3.5"
            y="9"
            width="13"
            height="9"
            rx="2"
            stroke="currentColor"
            strokeWidth="1.4"
        />
        <path
            d="M6.5 9V6.5a3.5 3.5 0 0 1 7 0V9"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
        />
        <circle cx="10" cy="13.5" r="1.25" fill="currentColor" />
    </svg>
);

const IconSpinner = ({ size = 20 }) => (
    <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        fill="none"
        aria-hidden="true"
        className="rd-spinner-svg"
    >
        <circle
            cx={size / 2}
            cy={size / 2}
            r={size / 2 - 2}
            stroke="currentColor"
            strokeWidth="2"
            strokeOpacity="0.2"
        />
        <path
            d={`M${size / 2} 2A${size / 2 - 2} ${size / 2 - 2} 0 0 1 ${size - 2} ${size / 2}`}
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
        />
    </svg>
);

// ─── Score ring ───────────────────────────────────────────────────────────────
function ScoreRing({ score, size = 80 }) {
    const r = (size - 10) / 2;
    const c = 2 * Math.PI * r;
    const pct = score != null ? Math.min(Math.max(score / 10, 0), 1) : 0;
    const off = c - pct * c;

    return (
        <div className="rd-score-ring" style={{ width: size, height: size }}>
            <svg width={size} height={size} aria-hidden="true">
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    fill="none"
                    stroke="rgba(255,255,255,0.12)"
                    strokeWidth="6"
                />
                {score != null && (
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={r}
                        fill="none"
                        stroke="rgba(255,255,255,0.85)"
                        strokeWidth="6"
                        strokeDasharray={c}
                        strokeDashoffset={off}
                        strokeLinecap="round"
                        style={{
                            transform: "rotate(-90deg)",
                            transformOrigin: "50% 50%",
                            transition:
                                "stroke-dashoffset 1s cubic-bezier(0.16,1,0.3,1)",
                        }}
                    />
                )}
            </svg>
            <div className="rd-score-ring__value">
                {score != null ? (
                    <>
                        <span className="rd-score-ring__num">{score}</span>
                        <span className="rd-score-ring__denom">/10</span>
                    </>
                ) : (
                    <span className="rd-score-ring__pending">—</span>
                )}
            </div>
        </div>
    );
}

// ─── Criteria meta ────────────────────────────────────────────────────────────
const CRITERIA_META = [
    { key: "structure_clarity", label: "Structure & clarity" },
    { key: "communication_quality", label: "Communication quality" },
    {
        key: "relevance_to_question",
        label: "Relevance to the question",
    },
];

function CriteriaItem({ label, text, status, error }) {
    const body =
        status === "ready"
            ? text || "No feedback provided."
            : status === "error"
              ? error || "Feedback failed to generate."
              : "Generating feedback…";
    // todo: change copy when user is on free plan: "Transcripting..."

    return (
        <div className="rd__criteria-item">
            <div className="rd__criteria-item__header">
                <span className="rd__criteria-item__label">{label}</span>
            </div>
            <p className="rd__criteria-item__body">{body}</p>
        </div>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function RecordingDetailPage() {
    const { qid, recordingId } = useParams();
    const questionSlug = qid;
    const navigate = useNavigate();
    const { isAuthenticated, loginWithRedirect, getAccessTokenSilently } =
        useAuth0();

    const [token, setToken] = useState(null);
    const [loading, setLoading] = useState(true);
    const [row, setRow] = useState(null);
    const [err, setErr] = useState(null);
    const [autoScroll, setAutoScroll] = useState(false);
    const [plan, setPlan] = useState(null);
    const [pendingDelete, setPendingDelete] = useState(false);

    const videoRef = useRef(null);
    const transcriptRef = useRef(null);
    const lineRefs = useRef([]);
    const lastIndexRef = useRef(-1);

    const apiBase = API_BASE;
    const preventTranscriptCopy = (event) => {
        event.preventDefault();
    };

    // Auth token
    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!isAuthenticated) {
                await loginWithRedirect();
                return;
            }
            try {
                const t = await getAccessTokenSilently({
                    authorizationParams: {
                        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                    },
                });
                if (!cancelled) setToken(t);
            } catch (e) {
                if (!cancelled) setErr(e?.message || "Auth failed");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, loginWithRedirect, getAccessTokenSilently]);

    // Load recording
    useEffect(() => {
        if (!token) return;
        const ac = new AbortController();
        setLoading(true);
        setErr(null);
        (async () => {
            try {
                const res = await fetch(
                    `${apiBase}/me/recordings/${recordingId}`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                        signal: ac.signal,
                    }
                );
                if (!res.ok) {
                    const t = await res.text();
                    throw new Error(t || `${res.status}`);
                }
                setRow(await res.json());
            } catch (e) {
                if (e.name !== "AbortError")
                    setErr(e.message || "Failed to load");
            } finally {
                setLoading(false);
            }
        })();
        return () => ac.abort();
    }, [token, apiBase, recordingId]);

    // Load plan
    useEffect(() => {
        if (!token) return;
        const ac = new AbortController();
        (async () => {
            try {
                const res = await fetch(`${apiBase}/profile`, {
                    headers: { Authorization: `Bearer ${token}` },
                    signal: ac.signal,
                });
                if (!res.ok) return;
                const data = await res.json();
                setPlan(data?.plan || "free");
            } catch {}
        })();
        return () => ac.abort();
    }, [token, apiBase]);

    const title = useMemo(
        () =>
            row?.question_slug
                ? prettyTitle(row.question_slug)
                : prettyTitle(questionSlug),
        [row, questionSlug]
    );

    const resolveMediaUrl = (url) => {
        if (!url) return null;
        return /^https?:\/\//i.test(url) ? url : `${apiBase}${url}`;
    };

    const transcriptLines = useMemo(() => {
        let segments =
            row?.transcript_segments ||
            row?.transcript?.segments ||
            row?.segments;
        if (typeof segments === "string") {
            try {
                segments = JSON.parse(segments);
            } catch {
                segments = null;
            }
        }
        if (Array.isArray(segments) && segments.length > 0) {
            return segments.map((seg) => ({
                time: fmtTimestamp(seg.start ?? seg.start_time),
                text: seg.text || "",
                startSec: Number(seg.start ?? seg.start_time),
            }));
        }
        const text =
            row?.transcript_text ||
            row?.transcript ||
            row?.transcript?.text ||
            "";
        if (typeof text === "string" && text.trim()) {
            return text
                .split(/\n+/)
                .filter(Boolean)
                .map((line) => ({
                    time: "—",
                    text: line.trim(),
                    startSec: null,
                }));
        }
        return [];
    }, [row]);

    const hasTimedTranscript = useMemo(
        () => transcriptLines.some((l) => Number.isFinite(l.startSec)),
        [transcriptLines]
    );

    const feedback = useMemo(() => {
        const sectionsRaw =
            row?.feedback_sections ||
            row?.feedback?.sections ||
            row?.feedback?.criteria;
        let sections = sectionsRaw;
        if (typeof sectionsRaw === "string") {
            try {
                sections = JSON.parse(sectionsRaw);
            } catch {
                sections = null;
            }
        }
        return {
            verdict: row?.feedback_verdict || row?.feedback?.verdict || "",
            score: row?.feedback_score ?? row?.feedback?.score ?? null,
            sections: sections || {},
        };
    }, [row]);

    const isProAi =
        String(plan || "")
            .toLowerCase()
            .replace(/[\s-]/g, "_") === "pro_ai";
    const showLocked = !isProAi;

    useEffect(() => {
        lineRefs.current = [];
        lastIndexRef.current = -1;
    }, [transcriptLines.length]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video || !hasTimedTranscript) return;
        const handler = () => {
            const time = video.currentTime;
            let idx = -1;
            for (let i = transcriptLines.length - 1; i >= 0; i--) {
                const s = transcriptLines[i]?.startSec;
                if (Number.isFinite(s) && time >= s) {
                    idx = i;
                    break;
                }
            }
            if (idx < 0 || idx === lastIndexRef.current) return;
            lastIndexRef.current = idx;
            if (autoScroll)
                lineRefs.current[idx]?.scrollIntoView({
                    block: "nearest",
                    behavior: "smooth",
                });
        };
        video.addEventListener("timeupdate", handler);
        return () => video.removeEventListener("timeupdate", handler);
    }, [autoScroll, transcriptLines, hasTimedTranscript]);

    const onDownload = async () => {
        const baseName = (row?.filename || `recording-${recordingId}`).replace(
            /\.\w+$/,
            ""
        );
        const filename = `${baseName}.mp4`;
        const url = `${apiBase}/me/recordings/${recordingId}/download?format=mp4`;
        await downloadFile({ url, filename, token });
    };

    const confirmDelete = async () => {
        if (!token) {
            setPendingDelete(false);
            return;
        }
        const res = await fetch(`${apiBase}/me/recordings/${recordingId}`, {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
        });
        setPendingDelete(false);
        if (res.ok) navigate(`/dashboard/questions/${questionSlug}`);
        else alert("Delete failed.");
    };

    const feedbackStatus = row?.feedback_status;
    const transcriptStatus = row?.transcript_status;
    const transcriptError = row?.transcript_error;

    if (loading)
        return (
            <div className="rd">
                <div className="rd__loading" aria-live="polite">
                    <IconSpinner size={24} /> <span>Loading recording…</span>
                </div>
            </div>
        );

    if (err)
        return (
            <div className="rd">
                <div className="rd__error" role="alert">
                    <p className="rd__error-title">
                        Could not load this recording
                    </p>
                    <p className="rd__error-body">{err}</p>
                </div>
            </div>
        );

    return (
        <div className="rd">
            {/* Topbar */}
            <div className="rd__topbar">
                <nav className="q-shell__breadcrumb" aria-label="Breadcrumb">
                    <Link
                        to="/dashboard/questions"
                        className="q-shell__breadcrumb-back"
                    >
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            aria-hidden="true"
                        >
                            <path
                                d="M10 3L5 8l5 5"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        Question bank
                    </Link>
                    <span
                        className="q-shell__breadcrumb-sep"
                        aria-hidden="true"
                    >
                        /
                    </span>
                    <Link
                        to={`/dashboard/questions/${questionSlug}`}
                        className="q-shell__breadcrumb-back"
                    >
                        Selected question
                    </Link>
                    <span
                        className="q-shell__breadcrumb-sep"
                        aria-hidden="true"
                    >
                        /
                    </span>
                    <span
                        className="q-shell__breadcrumb-current"
                        aria-current="page"
                    >
                        Evaluation
                    </span>
                </nav>
                <div className="rd__topbar-actions">
                    <button
                        className="rd__action-btn"
                        onClick={onDownload}
                        aria-label="Download recording"
                    >
                        <IconDownload /> Download
                    </button>
                    <button
                        className="rd__action-btn rd__action-btn--danger"
                        onClick={() => setPendingDelete(true)}
                        aria-label="Delete recording"
                    >
                        <IconTrash /> Delete
                    </button>
                </div>
            </div>

            {/* Page title */}
            <div className="rd__page-header">
                <h1 className="rd__question-title">{title}</h1>
                {row?.created_at && (
                    <span className="rd__date">
                        {formatDateTime(row.created_at)}
                    </span>
                )}
            </div>

            {/* Content grid */}
            <div className="rd__grid">
                {/* Left: video + transcript */}
                <div className="rd__media-col">
                    <div className="rd__video-wrap">
                        <video
                            className="rd__video"
                            src={resolveMediaUrl(row?.url) || undefined}
                            controls
                            playsInline
                            ref={videoRef}
                        />
                    </div>

                    <div className="rd__transcript-card">
                        <div className="rd__transcript-header">
                            <h2 className="rd__section-title">Transcript</h2>
                            <label
                                className="rd__toggle"
                                aria-label="Auto-scroll transcript"
                            >
                                <input
                                    type="checkbox"
                                    checked={autoScroll}
                                    onChange={(e) =>
                                        setAutoScroll(e.target.checked)
                                    }
                                    disabled={!hasTimedTranscript}
                                />
                                <span
                                    className="rd__toggle-track"
                                    aria-hidden="true"
                                >
                                    <span className="rd__toggle-thumb" />
                                </span>
                                <span className="rd__toggle-label">
                                    Auto-scroll
                                </span>
                            </label>
                        </div>

                        <div
                            className="rd__transcript"
                            ref={transcriptRef}
                            onCopy={preventTranscriptCopy}
                            onCut={preventTranscriptCopy}
                        >
                            {transcriptLines.length > 0 ? (
                                transcriptLines.map((line, idx) => (
                                    <div
                                        className="rd__line"
                                        key={`${line.time}-${idx}`}
                                        ref={(el) => {
                                            lineRefs.current[idx] = el;
                                        }}
                                    >
                                        <button
                                            type="button"
                                            className="rd__timestamp"
                                            onClick={() => {
                                                if (
                                                    Number.isFinite(
                                                        line.startSec
                                                    ) &&
                                                    videoRef.current
                                                ) {
                                                    videoRef.current.currentTime =
                                                        line.startSec;
                                                    videoRef.current.play();
                                                }
                                            }}
                                            disabled={
                                                !Number.isFinite(line.startSec)
                                            }
                                            aria-label={`Jump to ${line.time}`}
                                        >
                                            {line.time}
                                        </button>
                                        <p className="rd__line-text">
                                            {line.text}
                                        </p>
                                    </div>
                                ))
                            ) : (
                                <div className="rd__line rd__line--status">
                                    <span className="rd__timestamp rd__timestamp--dash">
                                        —
                                    </span>
                                    <p className="rd__line-text rd__line-text--muted">
                                        {transcriptStatus === "pending"
                                            ? "Transcript is being generated…"
                                            : transcriptStatus === "error"
                                              ? `Transcript failed.${transcriptError ? ` ${transcriptError}` : ""}`
                                              : "Transcript not available yet."}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right: feedback */}
                <aside className="rd__feedback-col">
                    {/* Verdict card */}
                    <div
                        className={`rd__verdict-card${showLocked ? " rd__verdict-card--empty" : ""}`}
                    >
                        <p className="rd__verdict-card__eyebrow">Feedback</p>
                        <div className="rd__verdict-body">
                            {!showLocked && (
                                <>
                                    <p className="rd__verdict-text">
                                        {feedbackStatus === "ready" ? (
                                            feedback.verdict ||
                                            "Feedback is ready."
                                        ) : feedbackStatus === "error" ? (
                                            "Feedback failed to generate."
                                        ) : (
                                            <span className="rd__verdict-pending">
                                                <IconSpinner size={16} />{" "}
                                                Generating feedback…
                                            </span>
                                        )}
                                    </p>
                                    <div className="rd__score-wrap">
                                        <ScoreRing
                                            score={
                                                feedbackStatus === "ready"
                                                    ? feedback.score
                                                    : null
                                            }
                                        />
                                        <span className="rd__score-label">
                                            Your score
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>
                        {showLocked && (
                            <div
                                className="rd__lock-overlay feedback-card"
                                role="note"
                            >
                                <div className="rd__lock-icon">
                                    <IconLock />
                                </div>
                                <p className="rd__lock-title">Pro AI feature</p>
                                <p className="rd__lock-body">
                                    Upgrade to unlock detailed feedback for each
                                    recording.
                                </p>
                                <Link
                                    to="/pricing"
                                    className="rd__lock-cta"
                                    onClick={navigateToTop}
                                >
                                    See plans
                                </Link>
                            </div>
                        )}
                    </div>

                    {/* Evaluation card */}
                    <div
                        className={`rd__eval-card${showLocked ? " rd__eval-card--locked rd__eval-card--empty" : ""}`}
                    >
                        <p className="rd__section-title rd__section-title--eval">
                            Evaluation
                        </p>
                        {!showLocked && (
                            <div className="rd__eval-body">
                                {CRITERIA_META.map(({ key, label }) => (
                                    <CriteriaItem
                                        key={key}
                                        label={label}
                                        // icon={icon}
                                        text={feedback.sections?.[key]}
                                        status={feedbackStatus}
                                        error={row?.feedback_error}
                                    />
                                ))}
                            </div>
                        )}
                        {showLocked && (
                            <div className="rd__lock-overlay" role="note">
                                <div className="rd__lock-icon">
                                    <IconLock />
                                </div>
                                <p className="rd__lock-title">Pro AI feature</p>
                                <p className="rd__lock-body">
                                    Upgrade to unlock detailed evaluation
                                    insights for each recording.
                                </p>
                                <Link
                                    to="/pricing"
                                    className="rd__lock-cta"
                                    onClick={navigateToTop}
                                >
                                    See plans
                                </Link>
                            </div>
                        )}
                    </div>
                </aside>
            </div>

            <ConfirmDialog
                open={pendingDelete}
                title="Delete this recording?"
                description="This action cannot be undone."
                confirmText="Delete"
                cancelText="Cancel"
                tone="danger"
                onCancel={() => setPendingDelete(false)}
                onConfirm={confirmDelete}
            />
        </div>
    );
}
